import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { XIcon, ArrowSquareOutIcon, UserIcon, PlusIcon, GenderMaleIcon, GenderFemaleIcon } from "@phosphor-icons/react";

function ParentPicker({ open, onClose, role, dog, onPicked }) {
  const wantSex = role === "sire" ? "male" : "female";
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", breed: "", dob: "", registration_number: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setQ(""); setRows([]); setCreating(false);
      setForm({ name: "", breed: dog?.breed || "", dob: "", registration_number: "" });
    }
  }, [open, dog]);

  useEffect(() => {
    if (!q.trim()) { setRows([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/data/master-pedigree/dog-search", { params: { q: q.trim(), sex: wantSex } })
        .then((r) => setRows((r.data || []).filter((d) => d.id !== dog?.id)))
        .catch(() => setRows([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, wantSex, dog]);

  const createAncestor = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/data/master-pedigree/ancestor", {
        name: form.name.trim(), breed: form.breed.trim(), sex: wantSex,
        dob: form.dob || null, registration_number: form.registration_number || null,
      });
      onPicked(r.data.id, r.data.name);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="parent-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral">Set {role} for {dog?.name}</DialogTitle>
          <DialogDescription className="text-[12px]">
            Search the registry, or create a pedigree-only ancestor record.
          </DialogDescription>
        </DialogHeader>
        {!creating ? (
          <div className="space-y-3">
            <Input autoFocus placeholder={`Search ${wantSex} dogs by name or reg #…`} value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="parent-search-input" />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {rows.map((d) => (
                <button key={d.id} type="button" onClick={() => onPicked(d.id, d.name)}
                  className="w-full text-left rounded-lg border border-[#EAEAEA] hover:border-gold bg-white px-3 py-2 transition-colors"
                  data-testid={`parent-pick-${d.id}`}>
                  <span className="text-[13px] font-semibold text-ink">{d.name}</span>
                  <span className="text-[11px] text-[#6B6B6B] block">
                    {d.breed}{d.registration_number ? ` · ${d.registration_number}` : ""}
                    {d.owner_name ? ` · owner: ${d.owner_name}` : ""}{d.pedigree_only ? " · pedigree-only" : ""}
                  </span>
                </button>
              ))}
              {q.trim() && rows.length === 0 && (
                <p className="text-[12px] text-[#6B6B6B] py-2 text-center">No {wantSex} dogs match "{q}".</p>
              )}
            </div>
            <Button variant="outline" className="rounded-full w-full" onClick={() => setCreating(true)} data-testid="parent-create-toggle">
              <PlusIcon size={13} className="mr-1" /> Create a pedigree-only ancestor instead
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-[11px]">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ancestor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px]">Breed *</Label>
                <Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="ancestor-breed" /></div>
              <div><Label className="text-[11px]">DOB (optional)</Label>
                <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="ancestor-dob" /></div>
            </div>
            <div><Label className="text-[11px]">Registration # (optional)</Label>
              <Input value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} data-testid="ancestor-reg" /></div>
            <p className="text-[11px] text-[#9A9A9A]">Sex is fixed to {wantSex} for a {role}. Pedigree-only records never appear in the marketplace.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1" onClick={() => setCreating(false)}>Back to search</Button>
              <Button className="rounded-full flex-1 bg-ink hover:bg-crimson text-white" disabled={busy || !form.name.trim() || !form.breed.trim()}
                onClick={createAncestor} data-testid="ancestor-create-submit">
                {busy ? "Creating…" : `Create & set as ${role}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Right-hand editor for a clicked node on the Master Pedigree graph. */
export default function PedigreeDogEditor({ node, nodesById, onClose, onChanged }) {
  const [picker, setPicker] = useState(null); // "sire" | "dam" | null
  const [busy, setBusy] = useState(false);

  if (!node) return null;
  const sire = node.sire_id ? nodesById[node.sire_id] : null;
  const dam = node.dam_id ? nodesById[node.dam_id] : null;

  const patch = async (payload, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/admin/data/master-pedigree/${node.id}/parents`, payload);
      toast.success(okMsg);
      setPicker(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ParentRow = ({ role, parent, Icon, color }) => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-[#EAEAEA] bg-white px-3 py-2">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1" style={{ color }}>
          <Icon size={11} weight="bold" /> {role}
        </p>
        {parent ? (
          <p className="text-[13px] font-semibold text-ink truncate">
            {parent.name}
            <span className="text-[11px] text-[#6B6B6B] font-normal">
              {parent.owner_name ? ` · ${parent.owner_name}` : parent.pedigree_only ? " · pedigree-only" : ""}
            </span>
          </p>
        ) : node[`${role}_id`] ? (
          <p className="text-[12px] text-[#6B6B6B] italic">Set (outside current breed filter)</p>
        ) : (
          <p className="text-[12px] text-[#9A9A9A] italic">Not recorded</p>
        )}
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" disabled={busy}
          onClick={() => setPicker(role)} data-testid={`set-${role}-btn`}>
          {node[`${role}_id`] ? "Change" : "Set"}
        </Button>
        {node[`${role}_id`] && (
          <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px] text-crimson border-crimson/40" disabled={busy}
            onClick={() => patch({ [`clear_${role}`]: true }, `${role === "sire" ? "Sire" : "Dam"} removed`)}
            data-testid={`clear-${role}-btn`}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="absolute top-3 right-3 z-20 w-[320px] max-w-[90vw] rounded-xl border border-gold/40 bg-white shadow-xl p-4" data-testid="pedigree-editor-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-spectral text-lg text-ink leading-tight truncate">{node.name}</h3>
          <p className="text-[11.5px] text-[#6B6B6B]">
            {node.breed}{node.sex ? ` · ${node.sex}` : ""}{node.registration_number ? ` · ${node.registration_number}` : ""}
            {node.pedigree_only ? " · pedigree-only" : ""}
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-[#9A9A9A] hover:text-ink" data-testid="pedigree-editor-close">
          <XIcon size={16} weight="bold" />
        </button>
      </div>

      <div className="mt-3 rounded-lg bg-cream/60 border border-[#EEE5C8] px-3 py-2" data-testid="pedigree-owner-row">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gold flex items-center gap-1">
          <UserIcon size={11} weight="bold" /> Owner
        </p>
        {node.owner_id ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12.5px] text-ink truncate">{node.owner_name || "—"} <span className="text-[11px] text-[#6B6B6B]">{node.owner_email}</span></p>
            <a href={`/admin/users?q=${encodeURIComponent(node.owner_email || "")}`} target="_blank" rel="noreferrer"
              className="text-[11px] font-semibold text-ink underline underline-offset-2 shrink-0" data-testid="pedigree-owner-link">
              View
            </a>
          </div>
        ) : (
          <p className="text-[12px] text-[#9A9A9A] italic">No owner (registry record)</p>
        )}
      </div>

      <div className="mt-3 space-y-2">
        <ParentRow role="sire" parent={sire} Icon={GenderMaleIcon} color="#1F6FEB" />
        <ParentRow role="dam" parent={dam} Icon={GenderFemaleIcon} color="#C1272D" />
      </div>

      <a href={`/dogs/${node.id}`} target="_blank" rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-ink hover:text-crimson"
        data-testid="pedigree-open-dog">
        Open dog page <ArrowSquareOutIcon size={12} weight="bold" />
      </a>

      <ParentPicker
        open={!!picker}
        role={picker || "sire"}
        dog={node}
        onClose={() => setPicker(null)}
        onPicked={(id, name) => patch({ [`${picker}_id`]: id }, `${name} set as ${picker}`)}
      />
    </div>
  );
}
